import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SubmissionStateEnum } from '../../../../models/users/enum/submission-state-enum';
import { BeneficiaryDetailsChanges } from '../../../beneficiaries/models/beneficiary-details-changes';
import { BehaviorSubject, Observable } from 'rxjs';
import { BeneficiarySubmissionUtils } from '../../../beneficiaries/utils/beneficiary-submission-utils';
import { CommonModule } from '@angular/common';
import { SubmissionDetailComponent } from '../submission-detail-grid/submission-detail/submission-detail.component';
import { PreviousNotApplicablePipe } from '../../pipes/previous-not-applicable.pipe';
import { SinSubmissionDetailComponent } from '../submission-detail-grid/sin-submission-detail/sin-submission-detail.component';

@Component({
  selector: 'app-beneficiary-details-individual-changes',
  standalone: true,
  imports: [CommonModule, SubmissionDetailComponent, SinSubmissionDetailComponent, PreviousNotApplicablePipe],
  templateUrl: './beneficiary-details-individual-changes.component.html',
  styleUrls: [
    '../../../../views/shared/components/member-beneficiary-submission-details/member-beneficiary-submission-details.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BeneficiaryDetailsIndividualChangesComponent implements OnChanges {
  @Input() beneficiaryChanges!: BeneficiaryDetailsChanges;

  private _formattedPersonalChanges = new BehaviorSubject<BeneficiaryDetailsChanges | null>(null);
  public formattedPersonalChanges$ = this._formattedPersonalChanges as Observable<BeneficiaryDetailsChanges>;

  private _formattedContactChanges = new BehaviorSubject<BeneficiaryDetailsChanges | null>(null);
  public formattedContactChanges$ = this._formattedContactChanges as Observable<BeneficiaryDetailsChanges>;

  private _hasPersonalChanges = new BehaviorSubject<boolean>(false);
  public hasPersonalChanges$ = this._hasPersonalChanges as Observable<boolean>;

  private _hasContactChanges = new BehaviorSubject<boolean>(false);
  public hasContactChanges$ = this._hasContactChanges as Observable<boolean>;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.beneficiaryChanges) {
      const personalChanges = new BeneficiaryDetailsChanges();
      const contactChanges = new BeneficiaryDetailsChanges();

      const isPersonalKey = (key: string) =>
        ['relationshipToMember', 'birthDate', 'socialInsuranceNumber'].includes(key);

      const isContactKey = (key: string) => key === 'email' || key === 'phoneNumber' || key?.startsWith('address');

      const filterChanges = (changeList: any[], predicate: (key: string) => boolean) =>
        changeList
          .filter(change => predicate(change.label))
          .map(c => BeneficiarySubmissionUtils.formatBeneficiaryDetailsChanges(c));

      personalChanges.new = filterChanges(this.beneficiaryChanges.new, isPersonalKey);
      personalChanges.previous = filterChanges(this.beneficiaryChanges.previous, isPersonalKey);

      contactChanges.new = filterChanges(this.beneficiaryChanges.new, isContactKey);
      contactChanges.previous = filterChanges(this.beneficiaryChanges.previous, isContactKey);

      this._hasPersonalChanges.next(personalChanges.new.some(c => c.hasChanged));
      this._hasContactChanges.next(contactChanges.new.some(c => c.hasChanged));

      this._formattedPersonalChanges.next(personalChanges);
      this._formattedContactChanges.next(contactChanges);
    }
  }

  protected readonly SubmissionStateEnum = SubmissionStateEnum;
}
